// ReceiveInventory.jsx
// Consolidated inventory receiving — supports PO-based and ad hoc (barcode-first) flows
// Entry points:
//   /inventory/receive          → ad hoc mode (barcode scan, pick product)
//   /inventory/receive?po=123   → PO mode (lines pre-filled from PO)
//   Also opened from PO detail "Receive" button

import React, { Component } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import BarcodeScanner from '../../common/BarcodeScanner';
import CheckoutDrawer from '../../common/CheckoutDrawer';
import InventoryAPI from '../../../services/inventoryService';
import PurchasingAPI from '../../../services/purchasingService';
import VendorAPI from '../../../services/vendorService';
import { isAllLocations as checkIsAllLocations, getLocationName } from '../../../util/locationUtils';
import { convertFromUTC } from '../../../util/convert_timezone';
import { toast } from 'react-toastify';
import ReactGA from 'react-ga4';
import { formatProductLabel } from '../../../util/productLabel';

const DESKTOP_BREAKPOINT = 1024;

class ReceiveInventory extends Component {
    constructor(props) {
        super(props);
        const poParam = props.initialPoId || null;

        this.state = {
            // Mode: 'po' or 'adhoc'
            mode: poParam ? 'po' : 'adhoc',

            // ============================================================
            // PO Mode state
            // ============================================================
            openPOs: [],
            selectedPOId: poParam || '',
            po: null,
            poLines: [],
            // PO-level receive fields
            vendorInvoiceNumber: '',
            receiveDate: new Date().toISOString().split('T')[0],
            receiveNotes: '',
            // Per-line data: { [line_id]: { quantity, actualCost, barcode, lotNumber } }
            lineData: {},

            // ============================================================
            // Ad Hoc Mode state
            // ============================================================
            barcode: '',
            barcodeError: '',
            existingItem: null,
            existingItems: [],
            // Scanner modal (mobile)
            showScanner: false,
            scannerError: null,
            // Product selection (cascading)
            categories: [],
            products: [],
            selectedCategoryId: '',
            selectedBrand: '',
            selectedProductName: '',
            selectedProductId: '',
            // Units of measure
            units: [],
            // Inline creation
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProductName: '',
            // Location selection (when viewing "All Locations")
            receiveLocationId: '',
            // Quantity & cost
            adhocQuantity: '',
            adhocCost: '',
            adhocLotNumber: '',
            // Receive unit — 'each' (base), 'package' (× package size), or 'case' (× case).
            // Conversion factors come from the selected product's packaging, not typed here.
            adhocPackMode: 'each',
            // Step: 'barcode' or 'product'
            adhocStep: 'barcode',

            // ============================================================
            // Shared state
            // ============================================================
            loading: true,
            submitting: false,
            error: null,
            isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT
        };
    }

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        await this.loadInitialData();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    loadInitialData = async () => {
        try {
            const promises = [
                InventoryAPI.getServiceCategories(),
                InventoryAPI.getProducts(this.props.typeId),
                InventoryAPI.getUnitsOfMeasure(),
                PurchasingAPI.getPurchaseOrders(this.props.typeId).catch(() => [])
            ];

            const [categories, products, units, openPOs] = await Promise.all(promises);

            const allPOs = (Array.isArray(openPOs) ? openPOs : []).map(po => ({
                ...po,
                purchase_order_id: po.purchase_order_id || po.id
            }));
            // Only show POs that can be received against
            const normalizedPOs = allPOs.filter(po =>
                po.status === 'submitted' || po.status === 'partial'
            );

            this.setState({
                categories: categories || [],
                products: products || [],
                units: units || [],
                openPOs: normalizedPOs,
                loading: false
            }, () => {
                // If we have a PO param, load it
                if (this.state.selectedPOId) {
                    this.loadPO(this.state.selectedPOId);
                }
            });
        } catch (error) {
            console.error('Error loading initial data:', error);
            this.setState({ loading: false });
        }
    };

    // =========================================================================
    // PO Mode
    // =========================================================================

    loadPO = async (poId) => {
        if (!poId) {
            this.setState({ po: null, poLines: [], lineData: {} });
            return;
        }
        try {
            const poData = await PurchasingAPI.getPurchaseOrder(poId);
            const raw = Array.isArray(poData) ? poData[0] : poData;
            const po = { ...raw, purchase_order_id: raw.purchase_order_id || raw.id };
            const poLines = (po.lines || []).map(l => ({ ...l, line_id: l.line_id || l.id }));

            const lineData = {};
            poLines.forEach(line => {
                const remaining = (parseInt(line.quantity_ordered, 10) || 0) - (parseInt(line.quantity_received, 10) || 0);
                if (remaining > 0) {
                    lineData[line.line_id] = {
                        quantity: String(remaining),
                        actualCost: line.unit_cost ? String(line.unit_cost) : '',
                        poCost: line.unit_cost ? parseFloat(line.unit_cost) : null,
                        barcode: '',
                        lotNumber: ''
                    };
                }
            });

            this.setState({ po, poLines, lineData });
        } catch (err) {
            console.error('Error loading PO:', err);
            toast.error('Failed to load purchase order.');
        }
    };

    handlePOChange = (e) => {
        const poId = e.target.value;
        this.setState({ selectedPOId: poId }, () => {
            this.loadPO(poId);
        });
    };

    handleLineChange = (lineId, field, value) => {
        this.setState(prev => ({
            lineData: {
                ...prev.lineData,
                [lineId]: { ...prev.lineData[lineId], [field]: value }
            }
        }));
    };

    handlePOReceive = async () => {
        const { lineData, vendorInvoiceNumber, receiveDate, receiveNotes, po, poLines } = this.state;
        const linesToReceive = [];
        for (const line of poLines) {
            const data = lineData[line.line_id];
            if (!data) continue;
            const qty = parseInt(data.quantity, 10);
            if (!qty || qty <= 0) continue;

            const remaining = (parseInt(line.quantity_ordered, 10) || 0) - (parseInt(line.quantity_received, 10) || 0);
            if (qty > remaining) {
                this.setState({ error: `Cannot receive more than ${remaining} for ${line.product_name || line.name}.` });
                return;
            }

            linesToReceive.push({
                line,
                quantity: qty,
                actualCost: data.actualCost ? parseFloat(data.actualCost) : null,
                barcode: data.barcode || null,
                lotNumber: data.lotNumber || null
            });
        }

        if (linesToReceive.length === 0) {
            this.setState({ error: 'Enter quantities to receive.' });
            return;
        }

        this.setState({ submitting: true, error: null });
        try {
            // PO receive endpoint handles both PO status update AND inventory creation
            // Pass all per-line data so the server can create inventory items with correct quantities
            await PurchasingAPI.receivePO(po.purchase_order_id, linesToReceive.map(l => ({
                lineId: l.line.line_id,
                quantity: l.quantity,
                actualCost: l.actualCost,
                barcode: l.barcode || null,
                lotNumber: l.lotNumber || null,
                vendorInvoiceNumber: vendorInvoiceNumber || null,
                receiveDate: receiveDate || null,
                notes: receiveNotes || null
            })));

            // Update vendor catalog prices where cost changed
            const costChanges = linesToReceive.filter(item =>
                item.actualCost !== null &&
                item.line.unit_cost !== null &&
                Math.abs(item.actualCost - parseFloat(item.line.unit_cost)) > 0.001 &&
                item.line.vendor_product_id
            );
            for (const item of costChanges) {
                try {
                    await VendorAPI.updateVendorProduct(item.line.vendor_product_id, {
                        unit_cost: item.actualCost
                    });
                } catch (vpErr) {
                    console.error(`Error updating vendor product ${item.line.vendor_product_id}:`, vpErr);
                }
            }
            if (costChanges.length > 0) {
                toast.info(`Vendor catalog updated with ${costChanges.length} price change${costChanges.length > 1 ? 's' : ''}.`);
            }

            const totalInventoryAdded = linesToReceive.reduce((sum, l) => {
                const unitQty = parseFloat(l.line.unit_quantity) || 1;
                return sum + (l.quantity * unitQty);
            }, 0);
            const totalOrdered = linesToReceive.reduce((s, l) => s + l.quantity, 0);
            const unitLabel = totalInventoryAdded !== totalOrdered
                ? ` (${totalInventoryAdded} units added to inventory)`
                : '';
            toast.success(`Received ${linesToReceive.length} line${linesToReceive.length > 1 ? 's' : ''}${unitLabel}.`);

            ReactGA.event({ category: 'Inventory', action: 'PO Receive', label: 'prod' });
            if (this.props.onSuccess) this.props.onSuccess();
            else if (this.props.onHide) this.props.onHide();
        } catch (err) {
            console.error('Error receiving PO:', err);
            const serverMsg = err?.response?.data?.error || err?.response?.data?.message || '';
            this.setState({
                submitting: false,
                error: serverMsg || 'Failed to receive. Please try again.'
            });
        }
    };

    // =========================================================================
    // Ad Hoc Mode — Barcode handling
    // =========================================================================

    handleBarcodeChange = (e) => {
        this.setState({ barcode: e.target.value, barcodeError: '', existingItem: null });
    };

    handleBarcodeSubmit = async (e) => {
        e.preventDefault();
        const { barcode } = this.state;
        if (!barcode.trim()) {
            this.setState({ barcodeError: 'Please enter a barcode' });
            return;
        }

        this.setState({ submitting: true, barcodeError: '' });
        try {
            const result = await InventoryAPI.lookupBarcode(barcode.trim(), this.props.typeId);
            if (result && result.found && result.items && result.items.length > 0) {
                const existingItem = result.items[0];
                this.setState({
                    existingItem,
                    existingItems: result.items,
                    adhocStep: 'product',
                    submitting: false,
                    selectedCategoryId: existingItem.category_id ? String(existingItem.category_id) : '',
                    selectedBrand: existingItem.brand || '',
                    selectedProductName: existingItem.product_name || existingItem.name || '',
                    selectedProductId: existingItem.product_id ? String(existingItem.product_id) : ''
                });
            } else {
                this.setState({ adhocStep: 'product', submitting: false, existingItem: null, existingItems: [] });
            }
        } catch (error) {
            if (error.response && error.response.status === 404) {
                this.setState({ adhocStep: 'product', submitting: false, existingItem: null, existingItems: [] });
            } else {
                console.error('Error checking barcode:', error);
                this.setState({ barcodeError: 'Error checking barcode. Please try again.', submitting: false });
            }
        }
    };

    // Scanner (mobile)
    handleOpenScanner = () => this.setState({ showScanner: true, scannerError: null });
    handleCloseScanner = () => this.setState({ showScanner: false, scannerError: null });
    handleScanSuccess = (rawValue) => {
        this.setState({ barcode: rawValue, showScanner: false }, () => {
            this.handleBarcodeSubmit({ preventDefault: () => {} });
        });
    };

    // PO line barcode scan
    handleLineScan = (lineId) => {
        this.setState({ scanningLineId: lineId, showScanner: true, scannerError: null });
    };
    handleLineScanSuccess = (rawValue) => {
        const { scanningLineId } = this.state;
        if (scanningLineId) {
            this.handleLineChange(scanningLineId, 'barcode', rawValue);
        }
        this.setState({ showScanner: false, scanningLineId: null });
    };

    // =========================================================================
    // Ad Hoc Mode — Product selection (cascading)
    // =========================================================================

    getUniqueBrands = () => {
        const { products, selectedCategoryId } = this.state;
        if (!selectedCategoryId) return [];
        return products
            .filter(p => String(p.category_id) === String(selectedCategoryId))
            .map(p => p.brand)
            .filter(brand => brand && brand.trim())
            .filter((brand, index, self) => self.findIndex(b => b.toLowerCase() === brand.toLowerCase()) === index)
            .sort((a, b) => a.localeCompare(b));
    };

    getSelectableProducts = () => {
        const { products, selectedCategoryId, selectedBrand } = this.state;
        if (!selectedCategoryId || !selectedBrand) return [];
        return products
            .filter(p =>
                String(p.category_id) === String(selectedCategoryId) &&
                p.brand && p.brand.toLowerCase() === selectedBrand.toLowerCase()
            )
            .map(p => ({
                name: p.product_name || p.name,
                id: p.product_id || p.id,
                variant: p.variant_label || '',
                sku: p.sku || ''
            }))
            .filter(p => p.name && p.name.trim())
            .sort((a, b) => (a.name + a.variant).localeCompare(b.name + b.variant));
    };

    handleCategoryChange = (e) => {
        this.setState({
            selectedCategoryId: e.target.value,
            selectedBrand: '', selectedProductName: '', selectedProductId: '',
            showAddBrand: false, newBrand: '', showAddProduct: false, newProductName: ''
        });
    };

    handleBrandChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({
                showAddBrand: true, selectedBrand: '', selectedProductName: '', selectedProductId: '',
                showAddProduct: false, newProductName: ''
            });
        } else {
            this.setState({
                selectedBrand: value, showAddBrand: false, newBrand: '',
                selectedProductName: '', selectedProductId: '', showAddProduct: false, newProductName: ''
            });
        }
    };

    handleNewBrandChange = (e) => this.setState({ newBrand: e.target.value, selectedBrand: e.target.value });
    handleCancelNewBrand = () => this.setState({ showAddBrand: false, newBrand: '', selectedBrand: '' });

    handleProductNameChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({ showAddProduct: true, selectedProductName: '', selectedProductId: '' });
        } else {
            const product = this.getSelectableProducts().find(p => String(p.id) === String(value));
            this.setState({
                selectedProductId: value,
                selectedProductName: product ? product.name : '',
                showAddProduct: false, newProductName: ''
            });
        }
    };

    handleSetPackMode = (mode) => this.setState({ adhocPackMode: mode });

    handleNewProductNameChange = (e) => this.setState({ newProductName: e.target.value, selectedProductName: e.target.value });
    handleCancelNewProduct = () => this.setState({ showAddProduct: false, newProductName: '', selectedProductName: '' });

    getUnitNameById = (unitId) => {
        if (!unitId) return '';
        const u = (this.state.units || []).find(x => String(x.unit_id) === String(unitId));
        return u ? (u.unit_name || u.unit_code || '') : '';
    };

    // Receive-as options + conversion derived from the SELECTED product's packaging.
    // base each (×1), package/sell-unit (× package size), case (× units_per_case × package size).
    getReceiveConversion = () => {
        const { products, selectedProductId, adhocPackMode } = this.state;
        const p = products.find(x => String(x.product_id || x.id) === String(selectedProductId));
        const packageSize = parseFloat(p?.base_units_per_sell_unit) || 1;
        const perCase = parseInt(p?.units_per_case, 10) || 1;
        const baseUnitName = this.getUnitNameById(p?.unit_id) || this.getSelectedCategoryUnitName() || 'units';
        const sellUnitName = (p?.sell_unit_id && this.getUnitNameById(p.sell_unit_id)) || baseUnitName;
        const modes = [{ mode: 'each', label: baseUnitName, factor: 1 }];
        if (packageSize > 1) modes.push({ mode: 'package', label: sellUnitName, factor: packageSize });
        if (perCase > 1) modes.push({ mode: 'case', label: 'Case', factor: perCase * packageSize });
        const active = modes.find(m => m.mode === adhocPackMode) || modes[0];
        return { modes, active, factor: active.factor, baseUnitName, sellUnitName, packageSize, perCase };
    };

    getSelectedCategoryUnitName = () => {
        const { categories, selectedCategoryId, units } = this.state;
        if (!selectedCategoryId) return '';
        const category = categories.find(c => String(c.category_id) === String(selectedCategoryId));
        if (category) {
            const unitId = category.default_unit_id || category.defaultUnitId;
            if (unitId) {
                const unit = units.find(u => String(u.unit_id) === String(unitId));
                return unit ? unit.unit_name : '';
            }
        }
        return '';
    };

    handleBackToBarcode = () => {
        this.setState({
            adhocStep: 'barcode',
            selectedCategoryId: '', selectedBrand: '', selectedProductName: '', selectedProductId: '',
            adhocQuantity: '', adhocCost: '', adhocLotNumber: '',
            adhocPackMode: 'each',
            showAddBrand: false, newBrand: '', showAddProduct: false, newProductName: '',
            existingItem: null, existingItems: []
        });
    };

    // =========================================================================
    // Ad Hoc Mode — Submit
    // =========================================================================

    handleAdhocReceive = async (e) => {
        e.preventDefault();
        const {
            barcode, selectedProductId, selectedProductName, selectedBrand,
            selectedCategoryId, adhocQuantity, adhocCost, adhocLotNumber,
            showAddBrand, showAddProduct, categories, receiveLocationId
        } = this.state;

        const isNewProduct = showAddBrand || showAddProduct;
        const isAllLocs = checkIsAllLocations(this.props.selectedLocationId);

        if (isAllLocs && !receiveLocationId) {
            alert('Please select a receiving location');
            return;
        }
        if (!adhocQuantity || parseFloat(adhocQuantity) <= 0) {
            alert('Please enter a valid quantity');
            return;
        }

        // Packages/cases are a receiving convenience — convert to base units (ml, sq ft)
        // before storing. Stock, usage, deductions and reorder all stay in the base unit.
        // Conversion factor comes from the product's packaging (package size × case).
        const { factor } = this.getReceiveConversion();
        const baseQuantity = parseFloat(adhocQuantity) * factor;
        const baseUnitCost = adhocCost ? (parseFloat(adhocCost) / factor) : null;

        this.setState({ submitting: true });
        try {
            let productId = selectedProductId;

            // Create new product if needed
            if (isNewProduct && !selectedProductId) {
                if (!selectedProductName?.trim()) { alert('Product name is required'); this.setState({ submitting: false }); return; }
                if (!selectedBrand?.trim()) { alert('Brand is required'); this.setState({ submitting: false }); return; }

                // No duplicate check: products are identified by SKU, and two products
                // may legitimately share a brand and name as size variants. The server
                // rejects a genuine SKU collision with a 409.

                let unitId = null;
                if (selectedCategoryId) {
                    const cat = categories.find(c => String(c.category_id) === String(selectedCategoryId));
                    if (cat) unitId = cat.default_unit_id || cat.defaultUnitId;
                }

                // New products created inline receive as the base unit; packaging
                // (sell unit / package size / case) is set later in the product form.
                const result = await InventoryAPI.createProduct({
                    name: selectedProductName.trim(),
                    brand: selectedBrand.trim(),
                    categoryId: parseInt(selectedCategoryId, 10),
                    unitId: unitId ? parseInt(unitId, 10) : null,
                    reorderPoint: 0,
                    installerId: this.props.typeId
                });
                productId = result.id || result.product_id;
            }

            if (!productId) {
                alert('Please select a product');
                this.setState({ submitting: false });
                return;
            }

            const locationId = isAllLocs ? receiveLocationId : (this.props.selectedLocationId || null);
            await InventoryAPI.receiveInventory({
                productId: parseInt(productId, 10),
                installerId: parseInt(this.props.typeId, 10),
                barcode: barcode.trim(),
                quantity: baseQuantity,
                unitCost: baseUnitCost,
                lotNumber: adhocLotNumber || null,
                locationId
            });

            ReactGA.event({ category: 'Inventory', action: 'Ad Hoc Receive', label: 'prod' });
            toast.success('Inventory received.');
            if (this.props.onSuccess) this.props.onSuccess();
            else if (this.props.onHide) this.props.onHide();
        } catch (error) {
            console.error('Error receiving inventory:', error);
            if (error.response && error.response.status === 409) {
                alert(error.response.data.message);
            } else {
                alert('Error receiving inventory. Please try again.');
            }
            this.setState({ submitting: false });
        }
    };

    // =========================================================================
    // Render — PO Mode
    // =========================================================================

    renderPOMode() {
        const { openPOs, selectedPOId, po, poLines, lineData,
                vendorInvoiceNumber, receiveDate, receiveNotes, submitting, error } = this.state;
        const isMobile = this.isMobile();

        // Filter to lines with remaining quantity
        const receivableLines = poLines.filter(line => {
            const remaining = (parseInt(line.quantity_ordered, 10) || 0) - (parseInt(line.quantity_received, 10) || 0);
            return remaining > 0;
        });

        const thStyle = {
            padding: '8px 10px', fontSize: '11px', textTransform: 'uppercase',
            letterSpacing: '0.5px', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)',
            textAlign: 'left', fontWeight: 600
        };
        const tdStyle = {
            padding: '10px', borderBottom: '1px solid var(--color-border-light)', fontSize: '13px',
            color: 'var(--color-text-primary)', verticalAlign: 'top'
        };

        return (
            <>
                {/* PO Selector */}
                <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>
                        Select Purchase Order
                    </Form.Label>
                    <Form.Control as="select" value={selectedPOId} onChange={this.handlePOChange}>
                        <option value="">Choose a PO...</option>
                        {openPOs.map(p => (
                            <option key={p.purchase_order_id} value={p.purchase_order_id}>
                                {p.po_number || `PO-${p.purchase_order_id}`} — {p.vendor_name || 'Unknown'}{p.expected_date ? ` — Due ${convertFromUTC(p.expected_date).format('MM/DD/YY')}` : ''}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>

                {po && (
                    <>
                        {error && <div className="alert alert-danger">{error}</div>}

                        {/* PO header info */}
                        <div style={{ background: 'var(--color-bg-panel)', borderLeft: '3px solid var(--color-brand-teal)', padding: '12px', marginBottom: '16px', fontSize: '13px' }}>
                            <strong>{po.vendor_name}</strong>
                            {po.expected_date && <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Expected: {convertFromUTC(po.expected_date).format('MM/DD/YYYY')}</span>}
                            {po.notes && <div style={{ color: 'var(--color-text-secondary)', marginTop: '4px' }}>{po.notes}</div>}
                        </div>

                        {/* PO-level receive fields */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                            <Form.Group style={{ flex: 1, minWidth: '150px' }}>
                                <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>
                                    Vendor Invoice #
                                </Form.Label>
                                <Form.Control type="text" size="sm" placeholder="Invoice or reference number"
                                    value={vendorInvoiceNumber}
                                    onChange={(e) => this.setState({ vendorInvoiceNumber: e.target.value })} />
                            </Form.Group>
                            <Form.Group style={{ flex: 0, minWidth: '150px' }}>
                                <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>
                                    Receive Date
                                </Form.Label>
                                <Form.Control type="date" size="sm" value={receiveDate}
                                    onChange={(e) => this.setState({ receiveDate: e.target.value })} />
                            </Form.Group>
                            <Form.Group style={{ flex: 1, minWidth: '150px' }}>
                                <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>
                                    Notes
                                </Form.Label>
                                <Form.Control type="text" size="sm" placeholder="Damage, short ship, etc."
                                    value={receiveNotes}
                                    onChange={(e) => this.setState({ receiveNotes: e.target.value })} />
                            </Form.Group>
                        </div>

                        {/* Line items table */}
                        {receivableLines.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                                <i className="fa-sharp fa-regular fa-check-circle fa-2x mb-2" style={{ color: 'var(--color-success)' }}></i>
                                <p>All items on this PO have been received.</p>
                            </div>
                        ) : (
                            <>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Product</th>
                                                <th style={thStyle}>Ordered</th>
                                                <th style={thStyle}>Prev Recv</th>
                                                <th style={thStyle}>Receiving</th>
                                                <th style={thStyle}>Unit Cost</th>
                                                <th style={thStyle}>Item Barcode</th>
                                                <th style={thStyle}>Lot #</th>
                                                <th style={thStyle}>Inventory</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {receivableLines.map(line => {
                                                const data = lineData[line.line_id] || {};
                                                const remaining = (parseInt(line.quantity_ordered, 10) || 0) - (parseInt(line.quantity_received, 10) || 0);
                                                const unitQty = parseFloat(line.unit_quantity) || 1;
                                                const receivingQty = parseInt(data.quantity, 10) || 0;
                                                const inventoryAdd = receivingQty * unitQty;
                                                const hasCostVariance = data.actualCost && data.poCost !== null &&
                                                    Math.abs(parseFloat(data.actualCost) - data.poCost) > 0.001;
                                                const unitCode = line.base_unit_code || line.default_unit_code || '';

                                                return (
                                                    <tr key={line.line_id}>
                                                        <td style={tdStyle}>
                                                            <div style={{ fontWeight: 500 }}>{line.product_name || line.name}</div>
                                                            <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                                                                {line.brand || ''}{line.vendor_sku ? ` — SKU: ${line.vendor_sku}` : ''}
                                                            </div>
                                                        </td>
                                                        <td style={tdStyle}>{line.quantity_ordered}</td>
                                                        <td style={tdStyle}>{line.quantity_received || 0}</td>
                                                        <td style={tdStyle}>
                                                            <Form.Control type="number" size="sm" min="0" max={remaining}
                                                                value={data.quantity || ''}
                                                                onChange={(e) => this.handleLineChange(line.line_id, 'quantity', e.target.value)}
                                                                style={{ width: '70px' }} />
                                                        </td>
                                                        <td style={tdStyle}>
                                                            <Form.Control type="number" size="sm" step="0.01" min="0"
                                                                value={data.actualCost || ''}
                                                                onChange={(e) => this.handleLineChange(line.line_id, 'actualCost', e.target.value)}
                                                                style={{
                                                                    width: '90px',
                                                                    borderColor: hasCostVariance ? 'var(--color-warning)' : undefined,
                                                                    backgroundColor: hasCostVariance ? 'var(--color-warning-light)' : undefined
                                                                }} />
                                                            {hasCostVariance && (
                                                                <div style={{ fontSize: '10px', color: 'var(--color-warning-dark)', marginTop: '2px' }}>
                                                                    PO: ${data.poCost.toFixed(2)}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                                <Form.Control type="text" size="sm" placeholder={isMobile ? 'Scan or type' : 'Enter barcode'}
                                                                    value={data.barcode || ''}
                                                                    onChange={(e) => this.handleLineChange(line.line_id, 'barcode', e.target.value)}
                                                                    style={{ width: '110px' }} />
                                                                {isMobile && (
                                                                    <Button variant="outline-secondary" size="sm"
                                                                        onClick={() => this.handleLineScan(line.line_id)}
                                                                        style={{ padding: '2px 8px' }}>
                                                                        <i className="fa-sharp fa-regular fa-camera"></i>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={tdStyle}>
                                                            <Form.Control type="text" size="sm"
                                                                value={data.lotNumber || ''}
                                                                onChange={(e) => this.handleLineChange(line.line_id, 'lotNumber', e.target.value)}
                                                                style={{ width: '90px' }} />
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {receivingQty > 0 ? (
                                                                <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-success)' }}>
                                                                    +{inventoryAdd}{unitCode ? ` ${unitCode}` : ''}
                                                                    {unitQty > 1 && (
                                                                        <div style={{ fontSize: '10px', color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                                                                            {receivingQty} × {unitQty} {unitCode}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                                    <button
                                        className="action-btn primary-btn"
                                        onClick={this.handlePOReceive}
                                        disabled={submitting}
                                        style={{
                                            background: 'var(--color-brand-teal)', color: 'var(--color-text-inverse)', border: '1px solid var(--color-brand-teal)',
                                            padding: '10px 24px', borderRadius: '6px', fontSize: '14px',
                                            fontWeight: 500, cursor: 'pointer'
                                        }}>
                                        {submitting ? (
                                            <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Receiving...</>
                                        ) : (
                                            <><i className="fa-sharp fa-regular fa-box-check mr-2"></i>Receive Items</>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}

                {!po && selectedPOId === '' && openPOs.length === 0 && (
                    <div className="text-center py-4 text-muted">
                        <p>No open purchase orders. Switch to Quick Receive to add inventory without a PO.</p>
                    </div>
                )}
            </>
        );
    }

    // =========================================================================
    // Render — Ad Hoc Mode
    // =========================================================================

    renderAdhocBarcodeStep() {
        const { barcode, barcodeError, submitting } = this.state;
        const isMobile = this.isMobile();

        return (
            <div>
                <Form onSubmit={this.handleBarcodeSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Barcode</Form.Label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Form.Control type="text" value={barcode}
                                onChange={this.handleBarcodeChange}
                                placeholder={this.isMobile() ? 'Scan or enter barcode...' : 'Enter barcode...'}
                                isInvalid={!!barcodeError}
                                autoFocus />
                            {isMobile && (
                                <Button variant="outline-secondary" onClick={this.handleOpenScanner}
                                    title="Scan barcode" style={{ padding: '8px 16px' }}>
                                    <i className="fa-sharp fa-regular fa-camera"></i>
                                </Button>
                            )}
                        </div>
                        {barcodeError && (
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {barcodeError}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    <Button type="submit" style={{ backgroundColor: 'var(--color-brand-teal)', borderColor: 'var(--color-brand-teal)' }}
                        disabled={submitting || !barcode.trim()} className="w-100">
                        {submitting ? (
                            <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Checking...</>
                        ) : 'Continue'}
                    </Button>
                </Form>
            </div>
        );
    }

    renderAdhocProductStep() {
        const {
            categories, selectedCategoryId, selectedBrand, selectedProductName, selectedProductId,
            showAddBrand, newBrand, showAddProduct, newProductName,
            adhocQuantity, adhocCost, adhocLotNumber, submitting, existingItem,
            adhocPackMode
        } = this.state;

        const existingBrands = this.getUniqueBrands();
        const existingProducts = this.getSelectableProducts();
        const hasBrandSelected = selectedBrand && selectedBrand.trim();
        const hasProductSelected = selectedProductName && selectedProductName.trim();
        const isNewProduct = showAddBrand || showAddProduct;
        const selectedUnitName = this.getSelectedCategoryUnitName();
        const showQuantityField = selectedProductId || (isNewProduct && hasProductSelected);

        // Receive-as options + factor from the product's packaging. Qty entered is in the
        // chosen receive unit; converted to base units (ml, sq ft) for the preview + stock.
        const conv = this.getReceiveConversion();
        const factor = conv.factor;
        const showReceiveAs = conv.modes.length > 1;
        const baseUnitLabel = (conv.baseUnitName || 'units').toLowerCase();
        const baseUnits = (parseFloat(adhocQuantity) || 0) * factor;
        const qtyUnitLabel = conv.active.mode === 'each' ? '' : conv.active.label;

        return (
            <div>
                <Button variant="link" className="p-0 mb-3" onClick={this.handleBackToBarcode}
                    style={{ color: 'var(--color-text-secondary)', textDecoration: 'none' }}>
                    <i className="fa-sharp fa-regular fa-arrow-left mr-2"></i>Back to barcode
                </Button>

                {existingItem && (
                    <Alert variant="info" className="mb-3">
                        <i className="fa-sharp fa-regular fa-info-circle mr-2"></i>
                        <strong>Barcode found:</strong> Currently linked to{' '}
                        <strong>{formatProductLabel(existingItem)}</strong>.
                        Confirm below or select a different product.
                    </Alert>
                )}

                <Form onSubmit={this.handleAdhocReceive}>
                    {/* Location picker when viewing All Locations */}
                    {checkIsAllLocations(this.props.selectedLocationId) && this.props.locations && (
                        <Form.Group className="mb-3">
                            <Form.Label>Receiving Location <span className="text-danger">*</span></Form.Label>
                            <Form.Control as="select" value={this.state.receiveLocationId}
                                onChange={(e) => this.setState({ receiveLocationId: e.target.value })}>
                                <option value="">Select location...</option>
                                {this.props.locations.map(loc => (
                                    <option key={loc.location_id} value={loc.location_id}>{getLocationName(loc)}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    )}

                    {/* Category */}
                    <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <Form.Control as="select" value={selectedCategoryId} onChange={this.handleCategoryChange}>
                            <option value="">Select a category...</option>
                            {[...new Map(categories.map(c => [c.category_id, c])).values()]
                                .sort((a, b) => (a.category_name || '').localeCompare(b.category_name || ''))
                                .map(cat => (
                                    <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
                                ))}
                        </Form.Control>
                    </Form.Group>

                    {/* Brand */}
                    {selectedCategoryId && (
                        <Form.Group className="mb-3">
                            <Form.Label>Brand</Form.Label>
                            {!showAddBrand ? (
                                <Form.Control as="select" value={selectedBrand} onChange={this.handleBrandChange}>
                                    <option value="">{existingBrands.length === 0 ? 'No brands — add new' : 'Select a brand...'}</option>
                                    {existingBrands.map(brand => (
                                        <option key={brand} value={brand}>{brand}</option>
                                    ))}
                                    <option value="add_new">+ Add New Brand</option>
                                </Form.Control>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <Form.Control type="text" value={newBrand} onChange={this.handleNewBrandChange}
                                        placeholder="Enter new brand name" autoFocus />
                                    <Button variant="outline-secondary" size="sm" className="ml-2"
                                        onClick={this.handleCancelNewBrand}>Cancel</Button>
                                </div>
                            )}
                        </Form.Group>
                    )}

                    {/* Product */}
                    {hasBrandSelected && (
                        <Form.Group className="mb-3">
                            <Form.Label>Product Name</Form.Label>
                            {!showAddProduct ? (
                                <Form.Control as="select" value={selectedProductId} onChange={this.handleProductNameChange}>
                                    <option value="">{existingProducts.length === 0 ? 'No products — add new' : 'Select a product...'}</option>
                                    {existingProducts.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}{p.variant ? ` (${p.variant})` : ''}{p.sku ? ` — ${p.sku}` : ''}
                                        </option>
                                    ))}
                                    <option value="add_new">+ Add New Product</option>
                                </Form.Control>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <Form.Control type="text" value={newProductName} onChange={this.handleNewProductNameChange}
                                        placeholder="Enter new product name" autoFocus />
                                    <Button variant="outline-secondary" size="sm" className="ml-2"
                                        onClick={this.handleCancelNewProduct}>Cancel</Button>
                                </div>
                            )}
                        </Form.Group>
                    )}

                    {/* Quantity, Cost, Lot */}
                    {showQuantityField && (
                        <>
                            {/* Received as: base unit / package (bottle, roll) / case — factors from the product */}
                            {showReceiveAs && (
                                <Form.Group className="mb-2">
                                    <Form.Label className="mb-1">Received as</Form.Label>
                                    <div className="btn-group btn-group-sm d-flex" role="group" style={{ maxWidth: '360px' }}>
                                        {conv.modes.map(m => (
                                            <button key={m.mode} type="button"
                                                className={`btn ${adhocPackMode === m.mode ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => this.handleSetPackMode(m.mode)}>
                                                {m.label}{m.factor > 1 ? ` (×${m.factor})` : ''}
                                            </button>
                                        ))}
                                    </div>
                                </Form.Group>
                            )}

                            <div style={{ display: 'flex', gap: '12px', marginBottom: factor > 1 && baseUnits > 0 ? '6px' : '16px' }}>
                                <Form.Group style={{ flex: 1 }}>
                                    <Form.Label>
                                        Quantity{' '}
                                        {qtyUnitLabel
                                            ? <small className="text-muted">({qtyUnitLabel})</small>
                                            : (selectedUnitName && <small className="text-muted">({selectedUnitName})</small>)}
                                    </Form.Label>
                                    <Form.Control type="number" step="0.01" min="0.01"
                                        value={adhocQuantity}
                                        onChange={(e) => this.setState({ adhocQuantity: e.target.value })}
                                        placeholder={qtyUnitLabel ? `No. of ${qtyUnitLabel.toLowerCase()}` : (selectedUnitName ? `Enter ${selectedUnitName.toLowerCase()}` : 'Enter quantity')} />
                                </Form.Group>
                                <Form.Group style={{ flex: 1 }}>
                                    <Form.Label>Cost{qtyUnitLabel ? ` / ${qtyUnitLabel.toLowerCase()}` : ' / unit'}</Form.Label>
                                    <Form.Control type="number" step="0.01" min="0"
                                        value={adhocCost}
                                        onChange={(e) => this.setState({ adhocCost: e.target.value })}
                                        placeholder="Optional" />
                                </Form.Group>
                            </div>

                            {factor > 1 && baseUnits > 0 && (
                                <div className="mb-3" style={{ fontSize: '13px', color: 'var(--color-success)', fontWeight: 500 }}>
                                    <i className="fa-sharp fa-regular fa-arrow-turn-down-right mr-2"></i>
                                    Adds {+baseUnits.toFixed(2)} {baseUnitLabel} to stock
                                    <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}> ({adhocQuantity} × {factor})</span>
                                    {adhocCost ? (
                                        <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}> · ${(parseFloat(adhocCost) / factor).toFixed(2)}/{baseUnitLabel.replace(/s$/, '')}</span>
                                    ) : null}
                                </div>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label>Lot Number <small className="text-muted">(optional)</small></Form.Label>
                                <Form.Control type="text" value={adhocLotNumber}
                                    onChange={(e) => this.setState({ adhocLotNumber: e.target.value })} />
                            </Form.Group>

                            <Button type="submit"
                                style={{ backgroundColor: 'var(--color-brand-teal)', borderColor: 'var(--color-brand-teal)' }}
                                disabled={submitting || !adhocQuantity} className="w-100">
                                {submitting ? (
                                    <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Receiving...</>
                                ) : (
                                    <><i className="fa-sharp fa-regular fa-box-check mr-2"></i>Receive Inventory</>
                                )}
                            </Button>
                        </>
                    )}
                </Form>
            </div>
        );
    }

    // =========================================================================
    // Render — Scanner Modal (shared)
    // =========================================================================

    renderScannerModal() {
        const { showScanner, scannerError, scanningLineId } = this.state;
        const onDetected = scanningLineId ? this.handleLineScanSuccess : this.handleScanSuccess;

        return (
            <Modal show={showScanner} onHide={this.handleCloseScanner} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa-sharp fa-regular fa-barcode mr-2"></i>Scan Barcode
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {scannerError ? (
                        <div className="p-4 text-center text-danger">
                            <i className="fa-sharp fa-regular fa-exclamation-triangle fa-2x mb-2"></i>
                            <p>{scannerError}</p>
                        </div>
                    ) : (
                        <div style={{ minHeight: 350 }}>
                            <BarcodeScanner
                                formats={['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code']}
                                onDetected={onDetected}
                                onError={(msg) => this.setState({ scannerError: msg })}
                                active={showScanner}
                                scanRegion={{ widthPercent: 0.75, heightPercent: 0.25 }}
                                throttleMs={100}
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleCloseScanner}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // =========================================================================
    // Render — Main
    // =========================================================================

    render() {
        const { mode, loading, openPOs } = this.state;
        const { show, onHide } = this.props;

        return (
            <React.Fragment>
                <CheckoutDrawer
                    show={show}
                    onHide={onHide}
                    title={<><i className="fa-sharp fa-regular fa-box-check mr-2"></i>Receive Inventory</>}
                    width={mode === 'po' ? 1000 : 680}
                >
                        {loading ? (
                            <div className="text-center py-5">
                                <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                                <p className="mt-3">Loading...</p>
                            </div>
                        ) : (
                            <>
                                {/* Mode Toggle */}
                                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                                    <div className="btn-group" role="group">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${mode === 'po' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => this.setState({ mode: 'po' })}>
                                            Receive Against PO
                                            {openPOs.length > 0 && (
                                                <span className="ml-1" style={{
                                                    background: mode === 'po' ? 'rgba(255,255,255,0.3)' : undefined,
                                                    padding: '1px 6px', borderRadius: '10px', fontSize: '11px'
                                                }}>
                                                    {openPOs.length}
                                                </span>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${mode === 'adhoc' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => this.setState({ mode: 'adhoc' })}>
                                            Quick Receive
                                        </button>
                                    </div>
                                </div>

                                {/* Content */}
                                {mode === 'po' ? this.renderPOMode() : (
                                    this.state.adhocStep === 'barcode'
                                        ? this.renderAdhocBarcodeStep()
                                        : this.renderAdhocProductStep()
                                )}
                            </>
                        )}
                </CheckoutDrawer>

                {this.renderScannerModal()}
            </React.Fragment>
        );
    }
}

export default ReceiveInventory;
